// <generated-client-wrappers:imports>
import type * as WrapperApi from '../generated/model/models';
import * as WrapperCore from '@angular/core';
import type * as WrapperHttp from '@angular/common/http';
import type * as WrapperRx from 'rxjs';
import * as WrapperServices from '../generated/api/appManagement.service';
// </generated-client-wrappers:imports>
import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { REQUEST_META_DATA } from '@seech-sub/core-ng';
import { Observable } from 'rxjs';
import { automationDriftHttpOptions, AutomationDriftRequestOptions } from './automation-drift-request-options';
import { OperationsApiRoutes } from '../constants/operations.constants';
import {
  AppClientDetail,
  AppClientQuery,
  AppClientSaveRequest,
  AppClientSummary,
  AppManagementPolicy,
  AppCredentialRotationResult,
  GeneratedKeyPairResult,
  GeneratedSecretResult,
  GenerateKeyPairRequest,
  PagedResult,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class AppManagementClientService {
  private readonly quietContext = new HttpContext().set(REQUEST_META_DATA.HIDE_PROGRESS, true);
  private readonly http = inject(HttpClient);

  getPolicy(): Observable<AppManagementPolicy> {
    return this.http.get<AppManagementPolicy>(OperationsApiRoutes.appsPolicy, {
      context: this.quietContext,
    });
  }

  queryApps(query: AppClientQuery): Observable<PagedResult<AppClientSummary>> {
    return this.http.post<PagedResult<AppClientSummary>>(OperationsApiRoutes.appsQuery, query, {
      context: this.quietContext,
    });
  }

  getApp(id: string): Observable<AppClientDetail> {
    return this.http.get<AppClientDetail>(OperationsApiRoutes.appById(id));
  }

  createApp(request: AppClientSaveRequest, options?: AutomationDriftRequestOptions): Observable<AppClientDetail> {
    return this.http.post<AppClientDetail>(OperationsApiRoutes.apps, request, automationDriftHttpOptions(options));
  }

  updateApp(id: string, request: AppClientSaveRequest, options?: AutomationDriftRequestOptions): Observable<AppClientDetail> {
    return this.http.put<AppClientDetail>(OperationsApiRoutes.appById(id), request, automationDriftHttpOptions(options));
  }

  enableApp(id: string, reason?: string, options?: AutomationDriftRequestOptions): Observable<AppClientDetail> {
    return this.http.post<AppClientDetail>(OperationsApiRoutes.appEnable(id), { reason }, automationDriftHttpOptions(options));
  }

  disableApp(id: string, reason?: string, options?: AutomationDriftRequestOptions): Observable<AppClientDetail> {
    return this.http.post<AppClientDetail>(OperationsApiRoutes.appDisable(id), { reason }, automationDriftHttpOptions(options));
  }

  generateAppSecret(): Observable<GeneratedSecretResult> {
    return this.http.post<GeneratedSecretResult>(OperationsApiRoutes.appGenerateSecret, {});
  }

  generateAppKeys(request: GenerateKeyPairRequest): Observable<GeneratedKeyPairResult> {
    return this.http.post<GeneratedKeyPairResult>(OperationsApiRoutes.appGenerateKeys, request);
  }

  rotateAppSecret(id: string, options?: AutomationDriftRequestOptions): Observable<AppCredentialRotationResult> {
    return this.http.post<AppCredentialRotationResult>(OperationsApiRoutes.appRotateSecret(id), {}, automationDriftHttpOptions(options));
  }

  rotateAppKeys(id: string, request: GenerateKeyPairRequest, options?: AutomationDriftRequestOptions): Observable<AppCredentialRotationResult> {
    return this.http.post<AppCredentialRotationResult>(OperationsApiRoutes.appRotateKeys(id), request, automationDriftHttpOptions(options));
  }
  // <generated-client-wrappers:methods>
  private readonly wrapperGeneratedClient = WrapperCore.inject(
    WrapperServices.AppManagementService,
    { optional: true },
  );

  create(
    request: WrapperApi.AppClientSaveRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementCreate(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  disable(
    id: string,
    request: WrapperApi.AppClientStatusRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementDisable(
      id,
      request,
      'body',
      reportProgress,
      options,
    );
  }

  enable(
    id: string,
    request: WrapperApi.AppClientStatusRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementEnable(
      id,
      request,
      'body',
      reportProgress,
      options,
    );
  }

  generateKeys(
    request: WrapperApi.GenerateKeyPairRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementGenerateKeys(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  generateSecret(
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementGenerateSecret(
      'body',
      reportProgress,
      options,
    );
  }

  get(
    id: string,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<WrapperApi.AppClientDetail> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementGet(
      id,
      'body',
      reportProgress,
      options,
    );
  }

  query(
    request: WrapperApi.AppClientQuery,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<WrapperApi.ObservabilityPageOfAppClientSummary> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementQuery(
      request,
      'body',
      reportProgress,
      options,
    );
  }

  rotateKeys(
    id: string,
    request: WrapperApi.GenerateKeyPairRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementRotateKeys(
      id,
      request,
      'body',
      reportProgress,
      options,
    );
  }

  rotateSecret(
    id: string,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementRotateSecret(
      id,
      'body',
      reportProgress,
      options,
    );
  }

  update(
    id: string,
    request: WrapperApi.AppClientSaveRequest,
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: '*/*';
      context?: WrapperHttp.HttpContext;
      transferCache?: boolean;
    },
  ): WrapperRx.Observable<Blob> {
    if (!this.wrapperGeneratedClient)
      throw new Error('AppManagementService is not available');
    return this.wrapperGeneratedClient.appManagementUpdate(
      id,
      request,
      'body',
      reportProgress,
      options,
    );
  }
  // </generated-client-wrappers:methods>
}
